import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { OverviewType, useAppSelector } from "../../app";
import { Status, doneStatuses } from "../../common/status/status";
import { BULK_ACTION_DISABLED_STATUSES } from "../constants";
import {
  BulkActionConfig,
  BulkActionValidationFlags,
} from "../interfaces/documentSet";
import { ADMIN_ROLE_ID } from "../../common/utilities/env";

const useBulkActionValidation = (selectedRows: string[]) => {
  const { t } = useTranslation("overview");
  const { documentSets, overviewType } = useAppSelector(
    (state) => state.documentSetsReducer,
  );
  const user = useAppSelector((state) => state.userReducer.user);

  const selectedDocuments = useMemo(() => {
    return documentSets.filter((doc) => selectedRows.includes(doc.id));
  }, [documentSets, selectedRows]);

  const validationFlags: BulkActionValidationFlags = useMemo(() => {
    const hasExportedDocs = selectedDocuments.some(
      (doc) => doc.status === Status.Exported,
    );

    const hasOnlyAllowedStatuses = selectedDocuments.every(
      (doc) =>
        !doneStatuses.includes(doc.status) ||
        doc.status === Status.Exported ||
        doc.status === Status.Archived,
    );

    const hasOnlyAssignedToCurrentUser = selectedDocuments.every(
      (doc) =>
        doc.status === Status.Exported ||
        !doc.assignee ||
        doc.assignee?.id === user?.id,
    );

    const hasNoRemoveStatus = selectedDocuments.every(
      (doc) => doc.status !== Status.Remove,
    );

    const hasNoProcessingStatus = selectedDocuments.every(
      (doc) => doc.status !== Status.Processing,
    );

    const canReExport = selectedDocuments.every(
      (doc) => doc.status === Status.Archived || doc.status === Status.Exported,
    );

    const canReApprove =
      !hasExportedDocs &&
      hasOnlyAssignedToCurrentUser &&
      selectedDocuments.every((doc) => doc.status === Status.ExportFailed);

    const canReprocess =
      selectedDocuments.every(
        (doc) =>
          ![...BULK_ACTION_DISABLED_STATUSES, Status.Exported].includes(
            doc.status,
          ),
      ) && hasOnlyAssignedToCurrentUser;

    const hasAssignedDocs = selectedDocuments.some((doc) => doc.assignee);

    return {
      hasExportedDocs,
      hasOnlyAssignedToCurrentUser,
      hasNoRemoveStatus,
      hasNoProcessingStatus,
      hasOnlyAllowedStatuses,
      canReExport,
      canReApprove,
      canReprocess,
      hasAssignedDocs,
    };
  }, [selectedDocuments, user?.id]);

  const canRemove = useMemo(() => {
    const {
      hasExportedDocs,
      hasOnlyAllowedStatuses,
      hasOnlyAssignedToCurrentUser,
      hasNoRemoveStatus,
    } = validationFlags;
    return (
      !hasExportedDocs &&
      hasOnlyAllowedStatuses &&
      hasOnlyAssignedToCurrentUser &&
      hasNoRemoveStatus
    );
  }, [validationFlags]);

  const canReject = useMemo(() => {
    return canRemove && validationFlags.hasNoProcessingStatus;
  }, [canRemove, validationFlags.hasNoProcessingStatus]);

  const isAdmin = useMemo(() => {
    return user?.access?.includes(ADMIN_ROLE_ID) ?? false;
  }, [user]);

  const showRemove = useMemo(() => {
    return (
      overviewType === OverviewType.Home ||
      overviewType === OverviewType.AnnotationTodo ||
      overviewType === OverviewType.AnnotationDone ||
      overviewType === OverviewType.Error
    );
  }, [overviewType]);

  const showReject = showRemove;

  const showReExport = useMemo(() => {
    return overviewType === OverviewType.Archived && isAdmin;
  }, [overviewType, isAdmin]);

  const showReApprove = useMemo(() => {
    return overviewType === OverviewType.Error && isAdmin;
  }, [overviewType, isAdmin]);

  const showReprocess = useMemo(() => {
    return (
      (overviewType === OverviewType.Home ||
        overviewType === OverviewType.AnnotationTodo ||
        overviewType === OverviewType.AnnotationDone ||
        overviewType === OverviewType.Error) &&
      isAdmin
    );
  }, [overviewType, isAdmin]);

  const showUnassign = useMemo(() => {
    return (
      overviewType === OverviewType.Home ||
      overviewType === OverviewType.AnnotationTodo ||
      overviewType === OverviewType.AnnotationDone ||
      overviewType === OverviewType.Error
    );
  }, [overviewType]);

  const getTooltip = useCallback(
    (action: string, enabled: boolean): string => {
      if (enabled) {
        return "";
      }

      const {
        hasExportedDocs,
        hasOnlyAssignedToCurrentUser,
        hasNoRemoveStatus,
        hasNoProcessingStatus,
        hasOnlyAllowedStatuses,
        canReExport,
        canReApprove,
        canReprocess,
      } = validationFlags;

      switch (action) {
        case "remove":
          if (hasExportedDocs) {
            return t(
              "bulkActionAlert.disabledExported",
              "Cannot perform this action on exported documents",
            );
          }
          if (!hasOnlyAssignedToCurrentUser) {
            return t(
              "bulkActionAlert.disabledAssignee",
              "Some documents are assigned to other users",
            );
          }
          if (!hasNoRemoveStatus) {
            return t(
              "bulkActionAlert.disabledRemoveStatus",
              "Some documents are already marked for removal",
            );
          }
          if (!hasOnlyAllowedStatuses) {
            return t(
              "bulkActionAlert.disabledStatus",
              "Some documents have incompatible statuses",
            );
          }
          break;
        case "reject":
          if (hasExportedDocs) {
            return t(
              "bulkActionAlert.disabledExported",
              "Cannot perform this action on exported documents",
            );
          }
          if (!hasNoProcessingStatus) {
            return t(
              "bulkActionAlert.disabledProcessing",
              "Cannot perform this action on documents being processed",
            );
          }
          if (!hasOnlyAssignedToCurrentUser) {
            return t(
              "bulkActionAlert.disabledAssignee",
              "Some documents are assigned to other users",
            );
          }
          if (!hasNoRemoveStatus) {
            return t(
              "bulkActionAlert.disabledRemoveStatus",
              "Some documents are already marked for removal",
            );
          }
          if (!hasOnlyAllowedStatuses) {
            return t(
              "bulkActionAlert.disabledStatus",
              "Some documents have incompatible statuses",
            );
          }
          break;
        case "reexport":
          if (!canReExport) {
            return t(
              "bulkActionAlert.disabledReExport",
              "Only archived or exported documents can be re-exported",
            );
          }
          break;
        case "reapprove":
          if (!hasOnlyAssignedToCurrentUser) {
            return t(
              "bulkActionAlert.disabledAssignee",
              "Some documents are assigned to other users",
            );
          }
          if (!canReApprove) {
            return t(
              "bulkActionAlert.disabledReApprove",
              "Only documents with export failed status can be re-approved",
            );
          }
          break;
        case "reprocess":
          if (hasExportedDocs) {
            return t(
              "bulkActionAlert.disabledExported",
              "Cannot perform this action on exported documents",
            );
          }
          if (!hasNoProcessingStatus) {
            return t(
              "bulkActionAlert.disabledProcessing",
              "Cannot perform this action on documents being processed",
            );
          }
          if (!hasOnlyAssignedToCurrentUser) {
            return t(
              "bulkActionAlert.disabledAssignee",
              "Some documents are assigned to other users",
            );
          }
          if (!canReprocess) {
            return t(
              "bulkActionAlert.disabledReprocess",
              "Some documents cannot be reprocessed",
            );
          }
          break;
        case "unassign":
          return t(
            "bulkActionAlert.disabledUnassign",
            "No documents with assignees selected",
          );
      }

      return "";
    },
    [validationFlags, t],
  );

  const removeConfig: BulkActionConfig = useMemo(
    () => ({
      action: "remove",
      enabled: canRemove,
      tooltip: getTooltip("remove", canRemove),
    }),
    [canRemove, getTooltip],
  );

  const rejectConfig: BulkActionConfig = useMemo(
    () => ({
      action: "reject",
      enabled: canReject,
      tooltip: getTooltip("reject", canReject),
    }),
    [canReject, getTooltip],
  );

  const reExportConfig: BulkActionConfig = useMemo(
    () => ({
      action: "reexport",
      enabled: validationFlags.canReExport && isAdmin,
      tooltip: getTooltip("reexport", validationFlags.canReExport && isAdmin),
    }),
    [validationFlags.canReExport, getTooltip, isAdmin],
  );

  const reApproveConfig: BulkActionConfig = useMemo(
    () => ({
      action: "reapprove",
      enabled: validationFlags.canReApprove && isAdmin,
      tooltip: getTooltip("reapprove", validationFlags.canReApprove && isAdmin),
    }),
    [validationFlags.canReApprove, getTooltip, isAdmin],
  );

  const reprocessConfig: BulkActionConfig = useMemo(
    () => ({
      action: "reprocess",
      enabled: validationFlags.canReprocess && isAdmin,
      tooltip: getTooltip("reprocess", validationFlags.canReprocess && isAdmin),
    }),
    [validationFlags.canReprocess, getTooltip, isAdmin],
  );

  const unassignConfig: BulkActionConfig = useMemo(
    () => ({
      action: "unassign",
      enabled: validationFlags.hasAssignedDocs,
      tooltip: getTooltip("unassign", validationFlags.hasAssignedDocs),
    }),
    [validationFlags.hasAssignedDocs, getTooltip],
  );

  return {
    removeConfig,
    rejectConfig,
    reExportConfig,
    reApproveConfig,
    reprocessConfig,
    unassignConfig,
    showRemove,
    showReject,
    showReExport,
    showReApprove,
    showReprocess,
    showUnassign,
    isAdmin,
  };
};

export default useBulkActionValidation;
